import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container,
  Button,
  Table,
  Modal,
  Form,
  ButtonGroup,
  Spinner,
  Alert,
  Row,
  Col,
  Card,
  Badge,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFile,
  faList,
  faMailBulk,
  faClockRotateLeft,
  faFilterCircleXmark,
  faChevronLeft,
  faChevronRight,
  faBackwardFast,
} from "@fortawesome/free-solid-svg-icons";
import {
  getLogs,
  getLogsByEmail,
  getLogsByFilename,
  getLogsCount,
} from "../../firebase";

const PAGE_SIZE = 25;

export default function GetLogs() {
  const [result, setResult] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(null);
  const [filterLabel, setFilterLabel] = useState("All logs · Latest first");
  const [filterMode, setFilterMode] = useState("all");
  const [pageState, setPageState] = useState({
    firstDoc: null,
    lastDoc: null,
    prevStack: [],
    hasNext: false,
    pageIndex: 1,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [pageInput, setPageInput] = useState("");

  const emailRef = useRef();
  const fileRef = useRef();

  useEffect(() => {
    loadTotalCount();
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadTotalCount() {
    try {
      const count = await getLogsCount();
      setTotalCount(count);
    } catch (err) {
      setTotalCount(0);
    }
  }

  async function fetchPage({
    direction = "next",
    cursor = null,
    stackAction = "reset",
  } = {}) {
    if (stackAction === "reset") {
      loadTotalCount();
    }
    setLoading(true);
    setError(null);

    try {
      const response = await getLogs({
        direction,
        cursor,
        pageSize: PAGE_SIZE,
      });
      setResult(response.entries);
      setFilterMode("all");
      setFilterLabel("All logs · Latest first");
      setPageState((prev) => {
        let nextPrev = prev.prevStack;
        let nextIndex = prev.pageIndex || 1;
        if (stackAction === "reset") {
          nextPrev = [];
          nextIndex = 1;
        } else if (stackAction === "push") {
          nextPrev = prev.firstDoc
            ? [...prev.prevStack, prev.firstDoc]
            : [...prev.prevStack];
          nextIndex = prev.pageIndex + 1;
        } else if (stackAction === "pop") {
          nextPrev = prev.prevStack.slice(0, -1);
          nextIndex = Math.max(1, prev.pageIndex - 1);
        }

        return {
          firstDoc: response.firstDoc,
          lastDoc: response.lastDoc,
          prevStack: nextPrev,
          hasNext: response.hasNext,
          pageIndex: nextIndex,
        };
      });
    } catch (err) {
      setError("Unable to load logs right now. Please try again.");
      setResult([]);
    } finally {
      setLoading(false);
    }
  }

  const hasPrev = pageState.pageIndex > 1;
  const hasNext = pageState.hasNext;
  const paginationEnabled = filterMode === "all";

  async function handleEmails(event) {
    event.preventDefault();
    const emailValue = emailRef.current.value.trim();
    if (!emailValue) return;
    setShowFilterModal(null);
    setLoading(true);
    setError(null);
    try {
      const data = await getLogsByEmail(emailValue);
      setResult(data);
      setFilterMode("email");
      setFilterLabel(`Logs for ${emailValue}`);
      setTotalCount(data.length);
      setPageState({
        firstDoc: null,
        lastDoc: null,
        prevStack: [],
        hasNext: false,
        pageIndex: 1,
      });
    } catch (err) {
      setError("Unable to load logs for that email.");
      setResult([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleFiles(event) {
    event.preventDefault();
    const fileValue = fileRef.current.value.trim();
    if (!fileValue) return;
    setShowFilterModal(null);
    setLoading(true);
    setError(null);
    try {
      const data = await getLogsByFilename(fileValue);
      setResult(data);
      setFilterMode("file");
      setFilterLabel(`Logs for “${fileValue}”`);
      setTotalCount(data.length);
      setPageState({
        firstDoc: null,
        lastDoc: null,
        prevStack: [],
        hasNext: false,
        pageIndex: 1,
      });
    } catch (err) {
      setError("Unable to load logs for that filename.");
      setResult([]);
    } finally {
      setLoading(false);
    }
  }

  function handleLogs(event) {
    if (event) {
      event.preventDefault();
    }
    setPageInput("");
    fetchPage({ stackAction: "reset" });
  }

  function handleNextPage() {
    if (!paginationEnabled || !hasNext || !pageState.lastDoc) return;
    fetchPage({
      direction: "next",
      cursor: pageState.lastDoc,
      stackAction: "push",
    });
  }

  function handlePrevPage() {
    if (!paginationEnabled || !hasPrev || !pageState.firstDoc) return;
    fetchPage({
      direction: "prev",
      cursor: pageState.firstDoc,
      stackAction: "pop",
    });
  }

  function handleFirstPage() {
    if (!paginationEnabled || pageState.pageIndex === 1) return;
    setPageInput("");
    fetchPage({ stackAction: "reset" });
  }

  async function goToPage(targetPage) {
    if (!paginationEnabled) return;
    if (targetPage === 1) {
      handleLogs();
      return;
    }

    setLoading(true);
    setError(null);
    try {
      let response = await getLogs({ pageSize: PAGE_SIZE });
      let prevStack = [];
      let currentIndex = 1;

      while (currentIndex < targetPage && response.hasNext) {
        if (response.firstDoc) {
          prevStack.push(response.firstDoc);
        }
        response = await getLogs({
          direction: "next",
          cursor: response.lastDoc,
          pageSize: PAGE_SIZE,
        });
        currentIndex += 1;
      }

      if (currentIndex !== targetPage) {
        setError(`Only ${currentIndex} pages available right now.`);
      }

      setResult(response.entries);
      setFilterMode("all");
      setFilterLabel("All logs · Latest first");
      setPageState({
        firstDoc: response.firstDoc,
        lastDoc: response.lastDoc,
        prevStack,
        hasNext: response.hasNext,
        pageIndex: currentIndex,
      });
    } catch (err) {
      setError("Unable to jump to that page.");
      setResult([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleGoToPage(event) {
    event.preventDefault();
    if (!paginationEnabled) return;
    const target = Number(pageInput);
    if (!Number.isFinite(target) || target < 1) {
      setError("Enter a valid page number.");
      return;
    }
    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    if (target > totalPages) {
      setError(`Page must be between 1 and ${totalPages}.`);
      return;
    }
    if (target === pageState.pageIndex) {
      return;
    }
    await goToPage(target);
    setPageInput("");
  }

  const currentPage = pageState.pageIndex ?? 1;
  const totalEntries = result.length;
  const latestRaw = result[0]?.[2] ?? null;
  const totalPages = paginationEnabled
    ? Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
    : 1;
  const entriesSoFar = paginationEnabled
    ? Math.min(totalCount, (currentPage - 1) * PAGE_SIZE + totalEntries)
    : totalEntries;
  const totalEntriesDisplay = paginationEnabled ? totalCount : totalEntries;

  const formattedLatest = useMemo(() => {
    if (!latestRaw) return "—";
    const parsed = new Date(latestRaw);
    return Number.isNaN(parsed.getTime()) ? latestRaw : parsed.toLocaleString();
  }, [latestRaw]);

  function formatTimestamp(value) {
    if (!value) return "—";
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
  }

  function FilterModal() {
    const isEmail = showFilterModal === "email";
    const isFile = showFilterModal === "file";
    if (!isEmail && !isFile) return null;

    return (
      <Modal
        show={Boolean(showFilterModal)}
        onHide={() => setShowFilterModal(null)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {isEmail ? "Filter logs by email" : "Filter logs by filename"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={isEmail ? handleEmails : handleFiles}>
            <Form.Group className="mb-4">
              <Form.Label>{isEmail ? "Email address" : "Filename"}</Form.Label>
              <Form.Control
                required
                ref={isEmail ? emailRef : fileRef}
                type={isEmail ? "email" : "text"}
                placeholder={isEmail ? "name@example.com" : "report.pdf"}
                autoFocus
                disabled={loading}
              />
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="outline-secondary"
                onClick={() => setShowFilterModal(null)}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                Apply filter
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <>
      <FilterModal />
      <Container fluid className="logs-page py-5">
        <Row className="justify-content-center">
          <Col xl={10} lg={11}>
            <div className="logs-hero mb-4">
              <div className="logs-hero-content">
                <Badge bg="light" className="logs-hero-badge text-dark">
                  Audit trail
                </Badge>
                <h1 className="logs-hero-title">Download activity</h1>
                <p className="logs-hero-subtitle">{filterLabel}</p>
              </div>
            </div>

            <Card className="logs-filter-card mb-4">
              <Card.Body>
                <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
                  <div>
                    <h2 className="logs-filter-title">Filter activity</h2>
                    <p className="logs-filter-subtitle">
                      Refine the audit trail by data source.
                    </p>
                  </div>
                  <Button
                    variant="outline-primary"
                    className="logs-filter-reset"
                    onClick={handleLogs}
                    disabled={loading}
                  >
                    <FontAwesomeIcon
                      icon={faFilterCircleXmark}
                      className="me-2"
                    />
                    Reset filters
                  </Button>
                </div>
                <ButtonGroup className="logs-filter-group mt-3 flex-wrap">
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShowFilterModal("email")}
                    disabled={loading}
                  >
                    <FontAwesomeIcon icon={faMailBulk} className="me-2" />
                    Filter by email
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShowFilterModal("file")}
                    disabled={loading}
                  >
                    <FontAwesomeIcon icon={faFile} className="me-2" />
                    Filter by filename
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={handleLogs}
                    disabled={loading}
                  >
                    <FontAwesomeIcon icon={faList} className="me-2" />
                    View latest logs
                  </Button>
                </ButtonGroup>
                <div className="d-flex align-items-center gap-2 mt-3 text-muted small">
                  <FontAwesomeIcon icon={faClockRotateLeft} />
                  <span>Latest event: {formattedLatest}</span>
                </div>
              </Card.Body>
            </Card>

            <Card className="logs-card">
              <Card.Body>
                {error && (
                  <Alert variant="danger" className="mb-3">
                    {error}
                  </Alert>
                )}
                {loading ? (
                  <div className="logs-loading">
                    <Spinner animation="border" variant="primary" />
                  </div>
                ) : result.length > 0 ? (
                  <>
                    <div className="table-responsive logs-table-wrapper">
                      <Table
                        responsive
                        hover
                        size="sm"
                        className="logs-table align-middle"
                      >
                        <thead>
                          <tr>
                            <th>Filename</th>
                            <th>Email</th>
                            <th>Timestamp</th>
                            <th>Activity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.map((entry, idx) => (
                            <tr key={`${entry?.[0]}-${entry?.[1]}-${idx}`}>
                              <td>{entry?.[0] || "—"}</td>
                              <td>{entry?.[1] || "—"}</td>
                              <td>{formatTimestamp(entry?.[2])}</td>
                              <td>
                                <Badge
                                  bg="primary"
                                  className="logs-activity-badge"
                                >
                                  {entry?.[3] || "—"}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    {paginationEnabled ? (
                      <div className="logs-pagination">
                        <div className="logs-pagination-header text-center text-md-start">
                          <span className="logs-pagination-status">
                            Page {currentPage} of {totalPages} • {entriesSoFar}{" "}
                            / {totalEntriesDisplay} entries
                          </span>
                        </div>
                        <Row className="logs-pagination-actions gy-2 gx-2 align-items-center justify-content-center mt-3">
                          <Col xs={12} md="auto">
                            <ButtonGroup className="w-100 flex-wrap justify-content-center">
                              <Button
                                variant="outline-secondary"
                                onClick={handleFirstPage}
                                disabled={loading || currentPage === 1}
                              >
                                <FontAwesomeIcon
                                  icon={faBackwardFast}
                                  className="me-2"
                                />
                                First
                              </Button>
                              <Button
                                variant="outline-secondary"
                                onClick={handlePrevPage}
                                disabled={loading || !hasPrev}
                              >
                                <FontAwesomeIcon
                                  icon={faChevronLeft}
                                  className="me-2"
                                />
                                Previous
                              </Button>
                            </ButtonGroup>
                          </Col>
                          <Col xs={12} md="auto">
                            <Form
                              className="logs-pagination-jump d-flex align-items-center justify-content-center gap-2"
                              onSubmit={handleGoToPage}
                            >
                              <Form.Control
                                size="sm"
                                type="number"
                                min={1}
                                max={totalPages}
                                value={pageInput}
                                onChange={(event) =>
                                  setPageInput(event.target.value)
                                }
                                placeholder="Page"
                                disabled={loading || totalPages <= 1}
                                className="text-center"
                              />
                              <Button
                                type="submit"
                                size="sm"
                                variant="primary"
                                disabled={loading || totalPages <= 1}
                              >
                                Go
                              </Button>
                            </Form>
                          </Col>
                          <Col xs={12} md="auto">
                            <Button
                              variant="primary"
                              onClick={handleNextPage}
                              disabled={loading || !hasNext}
                              className="w-100"
                            >
                              Next
                              <FontAwesomeIcon
                                icon={faChevronRight}
                                className="ms-2"
                              />
                            </Button>
                          </Col>
                        </Row>
                      </div>
                    ) : (
                      <div className="logs-pagination logs-pagination-single">
                        <span className="logs-pagination-status">
                          Showing {totalEntriesDisplay} / {totalEntriesDisplay}{" "}
                          entries
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <Alert variant="info" className="logs-empty">
                    No logs found for the selected filter.
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}
